import React from "react";
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Typography, Link } from '@mui/material';
// Local Definitions from 'definitions' file
import { publications } from "../../definitions";
import usersApi from '../../server/usersApi';

export const TASK_STATUS = {
  PENDING: 'Pending',
  TIMED: 'Timed',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  DISABLED: 'Disabled',
  LOCAL: 'Local'
};

export const RECURRENCE_RULE_OPTIONS = {
  NONE: 'None',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
};

export const DATE_RANGES = [
  { label: 'All', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'this_week' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
];

export const ORDER_BY_OPTIONS = [
  { label: 'Published Date', value: 'publishedTime' },
  { label: 'Distance', value: 'distance' }
];

export const ARTICLE_TYPES = [
  { label: "All", value: "all" },
  { label: "Planning", value: "planning" },
  { label: "Traffic", value: "traffic" },
  { label: "HGV", value: "hgv" },
  { label: "Licensing", value: "licensing" },
  { label: "Probate", value: "probate" },
  { label: "Legal", value: "legal" },
  { label: "Statutory", value: "statutory" },
  { label: "Other", value: "other" }
];

export const TASK_TYPES = ['Public Notices', 'Single Article', 'Topic Burst', 'Weather Article'];

export const calculateNextScheduledTime = (currentScheduledTime, recurrenceRule) => {
  if (!(currentScheduledTime instanceof Date) || isNaN(currentScheduledTime.getTime())) {
    throw new Error('Invalid currentScheduledTime: must be a valid Date object.');
  }

  let nextScheduledTime = new Date(currentScheduledTime);

  switch (recurrenceRule) {
    case 'Daily':
      nextScheduledTime.setDate(nextScheduledTime.getDate() + 1);
      break;
    case 'Weekly':
      nextScheduledTime.setDate(nextScheduledTime.getDate() + 7);
      break;
    case 'Monthly':
      const currentMonth = nextScheduledTime.getMonth();
      nextScheduledTime.setMonth(currentMonth + 1);

      // Handle end-of-month cases
      if (nextScheduledTime.getMonth() !== (currentMonth + 1) % 12) {
        nextScheduledTime.setDate(0); // Move to the last day of the previous month
      }
      break;
    case 'Yearly':
      const currentYear = nextScheduledTime.getFullYear();
      nextScheduledTime.setFullYear(currentYear + 1);

      // Handle leap year adjustments if needed
      if (nextScheduledTime.getMonth() === 1 && nextScheduledTime.getDate() === 29 && !isLeapYear(nextScheduledTime.getFullYear())) {
        nextScheduledTime.setDate(28);
      }
      break;
    default:
      throw new Error(`Unsupported recurrence rule: ${recurrenceRule}`);
  }

  // Set the time based on the current scheduled time
  nextScheduledTime.setHours(currentScheduledTime.getHours());
  nextScheduledTime.setMinutes(currentScheduledTime.getMinutes());
  nextScheduledTime.setSeconds(currentScheduledTime.getSeconds());

  return nextScheduledTime;
}

// Helper function to check for leap years
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

export const applyDateRangeFilter = (tasks, field, range) => {
  if (range === 'all') return tasks;

  const now = new Date();
  let startDate, endDate;

  switch (range) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      break;
    case 'yesterday':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'this_week':
      startDate = new Date(now.setDate(now.getDate() - now.getDay()));
      endDate = new Date(now.setDate(now.getDate() - now.getDay() + 7));
      break;
    case 'last_week':
      startDate = new Date(now.setDate(now.getDate() - now.getDay() - 7));
      endDate = new Date(now.setDate(now.getDate() - now.getDay()));
      break;
    case 'this_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    default:
      return tasks;
  }

  return tasks.filter(task => {
    const taskDate = new Date(task[field]);
    return taskDate >= startDate && taskDate < endDate;
  });
};

const getPublicationLabel = (writingStyle) => {
  const publication = publications.find(pub => pub.value === writingStyle);
  return publication ? publication.label : writingStyle;
};

// Function to generate links from output.messages
export const fetchMessageDetails = async (messages, user_id) => {
  // Support array of IDs OR array of objects
  const isArrayOfObjects =
    Array.isArray(messages) &&
    messages.length > 0 &&
    typeof messages[0] === 'object' &&
    !!messages[0].id;

  const originalById = isArrayOfObjects
    ? Object.fromEntries(messages.map(m => [m.id, m]))
    : {};

  const ids = isArrayOfObjects ? messages.map(item => item.id) : messages;

  // Fetch details, tolerate arrays OR single objects OR null
  const rawDetails = await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await usersApi.fetchUserMessageById(user_id, id);
        if (!res) return null;
        if (Array.isArray(res)) return res[0] || null;
        if (typeof res === 'object') return res;
        return null;
      } catch {
        return null;
      }
    })
  );

  // Build merged rows; if no detail, fall back to original client payload
  const merged = ids.map((id, i) => {
    const detail = rawDetails[i];                 // may be null
    const original = originalById[id] || {};      // {} for id arrays

    // Prefer server detail if present; otherwise synthesize minimal record
    const base = detail && detail.id
      ? detail
      : { id, ...original };

    // Safe date fields
    const created =
      base.created_date || base.created_at || base.created || original.created_at || null;

    const modified =
      base.modified_date || base.updated_at || base.modified || created || null;

    return {
      ...base,
      // Ensure we keep original “publish/topic/cms/url” hints for UI rendering
      url: original.url ?? base.url ?? null,
      publish: original.publish ?? base.publish ?? null,
      topic: original.topic ?? base.topic ?? null,
      cms: original.cms ?? base.cms ?? null,
      // Normalize dates the renderer expects
      created_date: created || null,
      modified_date: modified || null,
    };
  }).filter(Boolean);

  // Sort newest first with safe fallbacks
  merged.sort((a, b) => {
    const ad = new Date(a.modified_date || a.updated_at || a.created_date || a.created_at || 0).getTime();
    const bd = new Date(b.modified_date || b.updated_at || b.created_date || b.created_at || 0).getTime();
    return bd - ad;
  });

  return merged;
};

export const generateHistoryLinks = (sortedMessages, toastId) => {
  // helpers
  const get = (obj, path) =>
    path.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);
  const pick = (obj, ...paths) => {
    for (const p of paths) {
      const v = get(obj, p);
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };
  const formatDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');

  const derivePublishStatus = (msg) => {
    const attempted = pick(msg, 'publish.attempted', 'settings.publish.attempted');
    const succeeded = pick(msg, 'publish.succeeded', 'settings.publish.succeeded');
    const url = pick(msg, 'publish.url', 'settings.publish.url');
    const error = pick(msg, 'publish.error', 'settings.publish.error');
    const attemptedAtRaw = pick(msg, 'publish.attemptedAt', 'settings.publish.attemptedAt');

    const noSignals =
      attempted === undefined && succeeded === undefined && !url && !error && attemptedAtRaw === undefined;

    if (attempted === false || noSignals) {
      return {
        attempted: false,
        succeeded: null,
        label: 'Publishing: not attempted',
        tone: '#6b7280',
        bg: '#f3f4f6',
        attemptedAt: null,
        url: null,
        error: null,
      };
    }

    const isSuccess = succeeded === true || (!!url && !error);
    return {
      attempted: true,
      succeeded: isSuccess,
      label: isSuccess ? 'Published ✓' : 'Publish failed',
      tone: isSuccess ? '#16a34a' : '#dc2626',
      bg: isSuccess ? '#ecfdf5' : '#fef2f2',
      attemptedAt: attemptedAtRaw ? new Date(attemptedAtRaw) : null,
      url: url || null,
      error: isSuccess ? null : (error || null),
    };
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      padding: 16,
      borderRadius: 12,
      width: '100%',
      maxWidth: 720,
      maxHeight: '90vh',
      overflowY: 'auto',
      boxSizing: 'border-box',
      boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.08)',
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      marginBottom: 12,
      padding: 12,
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      backgroundColor: '#ffffff',
      boxSizing: 'border-box',
      transition: 'box-shadow 120ms ease, transform 120ms ease',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    titleLink: {
      fontWeight: 600,
      textDecoration: 'none',
      color: '#111827',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '70%',
    },
    statusPill: (tone, bg) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: 9999,
      background: bg,
      border: `1px solid ${tone}`,
      color: tone,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }),
    detailsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
    row: { fontSize: 14, color: '#374151', display: 'flex', gap: 6, alignItems: 'baseline', minWidth: 0 },
    label: { fontWeight: 700, color: '#111827' },
    value: { color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    ctaRow: { marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    primaryLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      textDecoration: 'none',
      background: '#1a73e8',
      color: 'white',
      fontWeight: 600,
      padding: '6px 10px',
      borderRadius: 8,
    },
    secondaryLink: { color: '#1a73e8', textDecoration: 'none', fontWeight: 500 },
  };

  return (
    <div style={styles.container}>
      {/* add CSS hover safely without hooks */}
      <style>{`
        .history-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
      `}</style>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={() => toast.remove(toastId)} style={{ marginBottom: 8 }}>
          Close
        </Button>
      </div>

      <div style={{ width: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        {sortedMessages.slice(0, 10).map((message, index) => {
          const topic = pick(message, 'topic', 'settings.topic');
          const topicId = pick(message, 'topic_id', 'settings.topic_id');

          const writingStyle = pick(message, 'settings.writingStyle', 'writingStyle');
          const cmsValue = pick(message, 'cms');
          const cmsLabel = writingStyle ? getPublicationLabel(writingStyle) : (cmsValue || '—');

          const publishedUrl = pick(message, 'publish.url', 'settings.publish.url');
          const cmsUrl = pick(message, 'url', 'settings.url');

          const created = pick(message, 'created_date', 'created_at', 'created');
          const modified = pick(message, 'modified_date', 'updated_at', 'modified');

          const pub = derivePublishStatus(message);
          const primaryHref = pub.url || publishedUrl || cmsUrl;
          const primaryText = pub.url || publishedUrl ? 'View Published Article' : (cmsUrl ? 'View in CMS' : null);
          const hasBoth = !!(publishedUrl && cmsUrl);

          return (
            <div key={message.id ?? index} className="history-card" style={styles.card}>
              {/* Header: Title + Publishing status */}
              <div style={styles.headerRow}>
                <Link
                  component={RouterLink}
                  to={`/ai-chat/${message.id}`}
                  target="_blank"
                  style={styles.titleLink}
                  title={topic || message.name || `Message ${index + 1}`}
                >
                  {topic || message.name || `Message ${index + 1}`}
                </Link>

                <div style={styles.statusPill(pub.tone, pub.bg)} title={pub.error || undefined}>
                  <span>{pub.label}</span>
                  {pub.attemptedAt && <span style={{ opacity: 0.8 }}>• {pub.attemptedAt.toLocaleString()}</span>}
                </div>
              </div>

              {/* Details with bold labels */}
              <div style={styles.detailsGrid}>
                {topic && (
                  <div style={styles.row}>
                    <span style={styles.label}>Topic:</span>
                    <span style={styles.value}>{topic}</span>
                  </div>
                )}
                {topicId && (
                  <div style={styles.row}>
                    <span style={styles.label}>Topic ID:</span>
                    <span style={styles.value}>{topicId}</span>
                  </div>
                )}
                <div style={styles.row}>
                  <span style={styles.label}>CMS:</span>
                  <span style={styles.value}>{cmsLabel}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Service Type:</span>
                  <span style={styles.value}>{pick(message, 'settings.serviceType') || '—'}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Created:</span>
                  <span style={styles.value}>{formatDateTime(created)}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Modified:</span>
                  <span style={styles.value}>{formatDateTime(modified)}</span>
                </div>
              </div>

              {/* CTA row */}
              {(primaryHref || hasBoth) && (
                <div style={styles.ctaRow}>
                  {primaryHref && primaryText && (
                    <a href={primaryHref} target="_blank" rel="noopener noreferrer" style={styles.primaryLink}>
                      {primaryText} <span aria-hidden>↗︎</span>
                    </a>
                  )}
                  {hasBoth && publishedUrl && cmsUrl && (
                    <a href={cmsUrl} target="_blank" rel="noopener noreferrer" style={styles.secondaryLink}>
                      Open in CMS
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {sortedMessages.length > 10 && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            and {sortedMessages.length - 10} more...
          </Typography>
        )}
      </div>
    </div>
  );
};
